/**
 * Zoho quote form: created when the visitor nears it, sized by Zoho's own
 * height messages, and replaced by the error card if Zoho stays silent.
 *
 * The frame is only made once the form is about a screen away, and only after
 * the page has stopped scrolling for a moment: every load is a view in the
 * client's Zoho reports, and a reader scrolling straight past the form must
 * not count as one.
 */
const AHEAD = '1000px 0px';
const SETTLE_MS = 250;
// Counted from the frame's load: a silent loaded frame has no form in it.
const SILENT_AFTER_LOAD = 8000;
// If the frame never loads at all, the error card shows this long after creation.
const NEVER_LOADED = 9000;

export default function initZoho() {
	document.querySelectorAll('[data-awdd-zoho]').forEach((wrap) => {
		// Only messages from the origin this wrap's own form is served from.
		let origin = '';
		try {
			origin = new URL(wrap.dataset.src, window.location.href).origin;
		} catch (err) {
			return;
		}
		const card = wrap.closest('.card');
		const error = card ? card.querySelector('[data-awdd-zoho-error]') : null;
		let frame = null;
		let heard = false;
		let silence = 0;

		const fail = () => {
			if (error) error.hidden = false;
		};

		const create = () => {
			if (frame) return;
			frame = document.createElement('iframe');
			frame.src = wrap.dataset.src;
			if (wrap.dataset.label) frame.title = wrap.dataset.label;
			frame.className = 'zoho-frame w-full';
			frame.style.height = '80vh';
			frame.addEventListener('error', () => {
				window.clearTimeout(silence);
				fail();
			});
			frame.addEventListener('load', () => {
				window.clearTimeout(silence);
				silence = window.setTimeout(() => {
					if (!heard) fail();
				}, SILENT_AFTER_LOAD);
			});
			silence = window.setTimeout(() => {
				if (!heard) fail();
			}, NEVER_LOADED);
			wrap.dataset.live = '1';
			wrap.appendChild(frame);
		};

		window.addEventListener('message', (e) => {
			if (!frame || e.origin !== origin || e.source !== frame.contentWindow || typeof e.data !== 'string') return;
			const parts = e.data.split('|');
			if ((parts.length !== 2 && parts.length !== 3) || parts[0] !== wrap.dataset.perma) return;
			const h = Math.ceil(Number(parts[1]));
			if (!Number.isFinite(h) || h <= 0) return;
			heard = true;
			window.clearTimeout(silence);
			if (error) error.hidden = true;
			frame.style.height = `${h}px`;
			frame.setAttribute('scrolling', 'no');
			// A third part: the resident moved to another page of the form.
			if (parts.length === 3) frame.scrollIntoView({ block: 'start' });
		});

		if (!('IntersectionObserver' in window)) {
			create();
			return;
		}

		let io = null;
		let timer = 0;
		const arm = () => {
			window.removeEventListener('scroll', settle);
			io = new IntersectionObserver(
				(entries) => {
					if (!entries.some((en) => en.isIntersecting)) return;
					io.disconnect();
					create();
				},
				{ rootMargin: AHEAD }
			);
			io.observe(wrap);
		};
		function settle() {
			window.clearTimeout(timer);
			timer = window.setTimeout(arm, SETTLE_MS);
		}
		window.addEventListener('scroll', settle, { passive: true });
		settle();
	});
}
